import React, { useCallback } from 'react';
import BaseTooltip from '../../ClipCustomizer/pocAIEdits/components/BaseTooltip';
import VideoTrackCard from './VideoTrackCard';
import { VideoOverlayTrack } from './types';
import { VideoElement } from '@/App/remotion/MainPlayer/types/Element/VideoElement';
import VideoElementReplace from '@/App/remotion/components/PlayerMenus/VideoElementMenu/VideoElementReplace';
import IconButton from '@/components/atoms/Button/IconButton';
import TooltipV2 from '@/components/TooltipV2';

interface VideoOverlayTooltipProps {
  element: VideoElement;
  triggerRef: React.RefObject<HTMLDivElement>;
  onDelete?: (elementId: string) => void;
}

/**
 * Maps a timeline VideoElement to VideoOverlayTrack format for use with VideoPreviewCard
 */
const mapVideoElementToVideoTrack = (element: VideoElement): VideoOverlayTrack => ({
  id: element.id,
  name: element.name || 'Video',
  thumbnailUrl: element.details?.preview || '',
  videoUrl: element.details?.src || '',
  duration: element.details?.duration || 0,
  tags: [] // Timeline elements don't have tags
});

/**
 * VideoOverlayTooltip - Enhanced tooltip for video overlay elements in the timeline.
 * Features a rich video preview with hover-to-play functionality while maintaining
 * the delete functionality from the original tooltip.
 */
const VideoOverlayTooltip: React.FC<VideoOverlayTooltipProps> = ({ element, triggerRef, onDelete }) => {
  const videoTrack = mapVideoElementToVideoTrack(element);

  const handleDelete = useCallback(
    (closeTooltip: () => void) => {
      if (onDelete) {
        onDelete(element.id);
      }
      closeTooltip();
    },
    [element.id, onDelete]
  );

  const renderVideoElementReplaceButton = useCallback(
    (handleReplace: (e: React.MouseEvent<Element, MouseEvent>) => void) => {
      return (
        <TooltipV2
          key="video-overlay-tooltip-replace-button"
          tooltipId="video-overlay-tooltip-replace-button"
          tooltipElement={
            <IconButton
              icon="IconReplace"
              onClick={handleReplace}
              buttonClassName="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md bg-white/90"
              trackingId="video-overlay-tooltip-replace-button"
            />
          }
          tooltipContent="Replace Video Overlay"
          place="top"
        />
      );
    },
    []
  );

  return (
    <BaseTooltip triggerRef={triggerRef} className="w-80">
      {closeTooltip => (
        <div className="w-full cursor-default">
          {/* Video Preview with floating action buttons */}
          <div className="relative w-full">
            <VideoTrackCard track={videoTrack} isSelected={false} onClick={undefined} showTooltip={false} />
            {/* Floating action buttons in top-right corner */}
            <div className="absolute right-0 top-0 z-30 flex gap-2 p-2">
              {onDelete && (
                <TooltipV2
                  key="video-overlay-tooltip-delete-button"
                  tooltipId="video-overlay-tooltip-delete-button"
                  tooltipContent="Delete Video Overlay"
                  tooltipElement={
                    <IconButton
                      icon="IconTrash"
                      onMouseDown={() => handleDelete(closeTooltip)}
                      buttonClassName="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md bg-white/90"
                      trackingId="video-overlay-tooltip-delete-button"
                    />
                  }
                />
              )}
              <VideoElementReplace selectedElement={element} renderButton={renderVideoElementReplaceButton} />
            </div>
          </div>
        </div>
      )}
    </BaseTooltip>
  );
};

export default VideoOverlayTooltip;
